import { useEffect, useState } from 'react';
import { LevelOneApprover, User } from '../services/Types';
import { getUsers } from '../services/AuthService';

// Global state management for users
let globalUsersState: User[] = [];
let globalUsersErrorState: string | null = null;
let globalUserUpdatingState: boolean = false;
let globalUserSubscribers: Set<(users: User[], error: string | null, updating: boolean) => void> = new Set();

// Function to notify all subscribers of state changes
const notifySubscribers = () => {
  globalUserSubscribers.forEach(callback => callback(globalUsersState, globalUsersErrorState, globalUserUpdatingState));
};

// Function to update global state
const updateGlobalState = (users: User[], error: string | null, updating: boolean) => {
  globalUsersState = users;
  globalUsersErrorState = error;
  globalUserUpdatingState = updating;
  notifySubscribers();
};

const emptyUser: User = {
  id: 0,
  firstName: '',
  lastName: '',
  email: '',
  role: 'USER',
  subRole: 'USER',
  approverLevel: 0,
  approvalStatus: 'PENDING',
  programOffice: {
    id: 0,
    title: '',
    acronym: '',
    status: 'ACTIVE',
    levelOneApprover: [],
  },
  events: 0,
  approvalEvents: 0,
  createdAt: '',
};

export const useAllUsers = () => {
  const [errorUseAllUsers, setErrorUseAllUsers] = useState<string | null>(globalUsersErrorState);
  const [users, setUsers] = useState<User[]>(globalUsersState);
  const [updating, setUpdating] = useState<boolean>(globalUserUpdatingState);

  useEffect(() => {
    // Subscribe to global state changes
    const handleStateChange = (newUsers: User[], newError: string | null, newUpdating: boolean) => {
      setUsers(newUsers);
      setErrorUseAllUsers(newError);
      setUpdating(newUpdating);
    };

    globalUserSubscribers.add(handleStateChange);

    // Load users if not already loaded
    if (globalUsersState.length === 0) {
      loadAllUsers();
    }

    // Cleanup subscription on unmount
    return () => {
      globalUserSubscribers.delete(handleStateChange);
    };
  }, []);

  // TODO: The cache should be for each user so we can reset a single user
  const resetCache = () => {
    updateGlobalState([], null, false);
    setTimeout(() => {
      loadAllUsers();
    }, 500);
  }

  const loadAllUsers = async () => {
    if (globalUsersState.length > 0) {
      return true;
    }
    if (globalUserUpdatingState) {
      return true;
    }
    try {
      updateGlobalState(globalUsersState, globalUsersErrorState, true);
      const { data } = await getUsers(0, '');
      if (data?.users) {
        updateGlobalState(data.users, null, false);
        return true;
      }
      return false;
    } catch (error) {
      const errorMessage = 'Error loading users: ' + error;
      updateGlobalState([], errorMessage, false);
      return false;
    }
  };

  // PO users
  const programOfficeUsers = (id: number) => {
    if (id === 0) {
      return [];
    }
    return globalUsersState.filter((user: User) => {
      if (!user.programOffice || !user.programOffice.id) {
        // eslint-disable-next-line no-console
        console.log('User has no programOffice:', user);
        return false;
      }
      return user.programOffice.id === id;
    });
  };

  const allApproverUsers = (id: number, level: number) => {
    if (id === 0) {
      return [];
    }
    if (level === 1) {
      return programOfficeUsers(id).filter((user: User) => {
        return user.role === 'APPROVER' && user.approverLevel === level;
      });
    }
    if (level >= 2) {
      return globalUsersState.filter((user: User) => {
        return user.role === 'APPROVER' && user.approverLevel >= level;
      });
    }
    return [];
  }

  const convertLevelOneApproversToUsers = (levelOneApprovers: LevelOneApprover[]) => {
    const usersFound = levelOneApprovers.map((approver: LevelOneApprover) => {
      return globalUsersState.find((user: User) => user.email === approver.email) || emptyUser;
    });
    return usersFound;
  };

  return {
    errorUseAllUsers,
    users,
    updating,
    programOfficeUsers,
    allApproverUsers,
    convertLevelOneApproversToUsers,
    resetCache,
  };
};